import axios from 'axios';

const FLYER_ENDPOINT = process.env.REACT_APP_FLYER_GENERATION_ENDPOINT;

function getEndpoint() {
  if (!FLYER_ENDPOINT) {
    throw new Error(
      'Flyer generation endpoint is not configured. Set REACT_APP_FLYER_GENERATION_ENDPOINT in your environment.'
    );
  }
  return FLYER_ENDPOINT;
}

async function postToFlyerEndpoint(payload) {
  try {
    const response = await axios.post(getEndpoint(), payload, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 90000
    });

    const result = response.data;

    if (!result?.success) {
      throw new Error(
        result?.message || 'Flyer endpoint did not return a success status.'
      );
    }

    return result;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
          `Flyer request failed (${error.response.status}).`
      );
    }
    if (error.request) {
      throw new Error(
        'No response from flyer service. Please check your connection.'
      );
    }
    throw new Error(
      error.message || 'An unexpected error occurred during flyer request.'
    );
  }
}

/**
 * Calls the AI generate+render flow (action: "generate_ai_flyer").
 * Gemini generates flyerContent AND Bannerbear renders the final image in one step.
 *
 * @param {Object} payload - output of buildAiFlyerRequest
 * @returns {Promise<{ success: boolean, flyerContent: object, s3Url: string, metadata: object }>}
 */
export async function generateAiFlyer(payload) {
  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed('[Flyer][AI request]');
    console.log('action:', payload.action);
    console.log('clientId:', payload.clientId);
    console.log('aiPrompt:', payload.aiPrompt);
    console.log('clientContext:', payload.clientContext);
    console.log('renderOptions:', payload.renderOptions);
    console.log('generationContext:', payload.generationContext);
    console.log('debugOptions:', payload.debugOptions);
    console.log('useLogo:', payload.brandOptions?.useLogo);
    console.log('hasLogoUrl:', Boolean(payload.brandOptions?.logoUrl));
    console.log('usePrimaryColor:', payload.brandOptions?.usePrimaryColor);
    console.log('primaryColor:', payload.brandOptions?.primaryColor);
    console.log('useSecondaryColor:', payload.brandOptions?.useSecondaryColor);
    console.log('secondaryColor:', payload.brandOptions?.secondaryColor);
    console.groupEnd();
  }

  const result = await postToFlyerEndpoint(payload);

  if (!result?.s3Url && !result?.dryRun) {
    throw new Error(
      result?.message || 'AI flyer generation did not return a valid image URL.'
    );
  }

  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed('[Flyer][AI response]');
    console.log('flyerContent:', result?.flyerContent);
    console.log('s3Url:', result?.s3Url);
    console.log('usedFallback:', result?.metadata?.usedFallback);
    console.log('selectedTemplateUid:', result?.metadata?.selectedTemplateUid);
    console.log(
      'selectedTemplateIndex:',
      result?.metadata?.selectedTemplateIndex
    );
    console.log('totalTemplates:', result?.metadata?.totalTemplates);
    console.log('regenerationCount:', result?.metadata?.regenerationCount);
    console.groupEnd();
  }

  return result;
}

/**
 * Calls the final render flow (action: "render_flyer").
 * Bannerbear renders the image and the result is uploaded to S3.
 *
 * @param {Object} payload - output of buildBannerbearRenderRequest
 * @returns {Promise<{ success: boolean, s3Url: string, metadata: object }>}
 */
export async function renderFlyer(payload) {
  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed('[Flyer][Manual render request]');
    console.log('action:', payload.action);
    console.log('clientId:', payload.clientId);
    console.log('flyerContent:', payload.flyerContent);
    console.log('clientContext:', {
      referenceImageUrl: payload.clientContext?.referenceImageUrl,
      hasLogoUrl: Boolean(payload.clientContext?.logoUrl)
    });
    console.log('renderOptions:', payload.renderOptions);
    console.log('generationContext:', payload.generationContext);
    console.log('debugOptions:', payload.debugOptions);
    console.log('useLogo:', payload.brandOptions?.useLogo);
    console.log('hasLogoUrl:', Boolean(payload.brandOptions?.logoUrl));
    console.log('usePrimaryColor:', payload.brandOptions?.usePrimaryColor);
    console.log('primaryColor:', payload.brandOptions?.primaryColor);
    console.log('useSecondaryColor:', payload.brandOptions?.useSecondaryColor);
    console.log('secondaryColor:', payload.brandOptions?.secondaryColor);
    console.groupEnd();
  }

  const result = await postToFlyerEndpoint(payload);

  if (!result?.s3Url && !result?.dryRun) {
    throw new Error(
      result?.message || 'Flyer render did not return a valid image URL.'
    );
  }

  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed('[Flyer][Manual render response]');
    console.log('s3Url:', result?.s3Url);
    console.log('selectedTemplateUid:', result?.metadata?.selectedTemplateUid);
    console.log(
      'selectedTemplateIndex:',
      result?.metadata?.selectedTemplateIndex
    );
    console.log('totalTemplates:', result?.metadata?.totalTemplates);
    console.log('regenerationCount:', result?.metadata?.regenerationCount);
    console.groupEnd();
  }

  return result;
}

/**
 * @deprecated Use renderFlyer() instead.
 * Kept for backward compatibility.
 */
export async function generateFlyer(payload) {
  return renderFlyer(payload);
}
